import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { CheckIcon } from './SelectIcons';
import { dropdownVariants, sizeStyles } from './selectStyles';
import type { SelectOption, SelectSize } from './selectTypes';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

interface SelectOptionListProps {
  /** Whether the dropdown is visible */
  isOpen: boolean;
  /** Available options */
  options: SelectOption[];
  /** Currently selected value */
  selectedValue: string;
  /** Index of the keyboard-highlighted option */
  highlightedIndex: number;
  /** Size variant */
  size: SelectSize;
  /** ID of the listbox element */
  listboxId: string;
  /** ID of the trigger that labels the listbox */
  labelledBy: string;
  /** Called with the value of a chosen option */
  onSelect: (value: string) => void;
  /** Called with the index of a hovered option */
  onHighlight: (index: number) => void;
}

/**
 * Aufklappliste der Auswahl.
 *
 * `AnimatePresence` bleibt hier drin, damit die `motion.ul` direktes Kind
 * bleibt und die Ausblendanimation unveraendert laeuft.
 */
export const SelectOptionList = forwardRef<HTMLUListElement, SelectOptionListProps>(
  (
    {
      isOpen,
      options,
      selectedValue,
      highlightedIndex,
      size,
      listboxId,
      labelledBy,
      onSelect,
      onHighlight,
    },
    ref
  ) => {
    const { t } = useTranslation();

    return (
    <AnimatePresence>
      {isOpen && (
        <motion.ul
          ref={ref}
          id={listboxId}
          role="listbox"
          aria-labelledby={labelledBy}
          variants={dropdownVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={cn(
            'absolute z-50 mt-2 w-full max-h-60 overflow-auto',
            'backdrop-blur-xl',
            'bg-surface-100/95',
            'border border-white/[0.12]',
            'rounded-xl',
            'shadow-2xl shadow-black/50',
            'py-2',
            'focus:outline-none'
          )}
        >
          {options.map((option, index) => {
            const isSelected = option.value === selectedValue;
            const isHighlighted = index === highlightedIndex;

            return (
              <li
                key={option.value}
                role="option"
                aria-selected={isSelected}
                aria-disabled={option.disabled}
                onClick={() => !option.disabled && onSelect(option.value)}
                onMouseEnter={() => !option.disabled && onHighlight(index)}
                className={cn(
                  'flex items-center justify-between gap-2 cursor-pointer',
                  sizeStyles[size].option,
                  'transition-colors duration-100',
                  isHighlighted && 'bg-white/[0.08]',
                  isSelected && 'text-primary',
                  !isSelected && 'text-surface-800',
                  option.disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                <span className="flex items-center gap-2">
                  {option.icon}
                  {option.label}
                </span>
                {isSelected && <CheckIcon />}
              </li>
            );
          })}

          {options.length === 0 && (
            <li className={cn(sizeStyles[size].option, 'text-surface-500')}>{t('a11y.noOptions')}</li>
          )}
        </motion.ul>
      )}
    </AnimatePresence>
    );
  }
);

SelectOptionList.displayName = 'SelectOptionList';
